"use client";

import { useEffect, useRef } from "react";
import Lenis from "lenis";
import { gsap, ScrollTrigger } from "@/utils/gsap";
import { ScrollController, useScrollStore } from "./useScrollStore";

export default function ScrollManager() {
  const smootherRef = useRef<Lenis | null>(null);
  const { setScroller } = useScrollStore();

  useEffect(() => {
    smootherRef.current = new Lenis({
      duration: 1.2,
      easing: (t: number) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
      wheelMultiplier: 1,
      touchMultiplier: 2,
    });

    smootherRef.current.on("scroll", ScrollTrigger.update);

    const rafCallback = (time: number) => {
      smootherRef.current?.raf(time * 1000);
    };

    gsap.ticker.add(rafCallback);
    gsap.ticker.lagSmoothing(0);

    const safeRefresh = () => {
      ScrollTrigger.refresh();
    };

    const controller: ScrollController = {
      start: () => {
        smootherRef.current?.start();
        document.body.style.overflowY = "auto";
      },
      stop: () => {
        if (!smootherRef.current) return;
        smootherRef.current.stop();
        document.body.style.overflowY = "hidden";
      },
      scrollTo: (y: number) => {
        smootherRef.current?.scrollTo(y, { duration: 1.5 });
      },
      reset: () => {
        smootherRef.current?.scrollTo(0, { immediate: true });
      },
      refresh: () => {
        safeRefresh();
      },
    };

    setScroller(controller);

    const handleLoad = () => {
      requestAnimationFrame(() => safeRefresh());
    };

    window.addEventListener("load", handleLoad);

    requestAnimationFrame(() => safeRefresh());

    return () => {
      gsap.ticker.remove(rafCallback);
      smootherRef.current?.destroy();
      window.removeEventListener("load", handleLoad);
      setScroller(null);
    };
  }, [setScroller]);

  return null;
}
